import { useState, useRef, useCallback, useEffect } from 'react';
import { Upload, AlertCircle, Loader2, Image as ImageIcon, GripVertical, Trash2, CheckCircle2 } from 'lucide-react';
import { compressForUpload, formatFileSize, type CompressionResult } from '../utils/image-compression';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

// Content image constraints (matches server-side)
const CONTENT_CONSTRAINTS = {
  maxSizeBytes: 2 * 1024 * 1024, // 2MB
  allowedFormats: ['image/webp', 'image/jpeg', 'image/png', 'image/gif'],
};

export interface ManagedImage {
  id?: string;
  url: string;
  r2Path?: string;
  altEn?: string;
  altId?: string;
  imageType: 'main' | 'gallery' | 'thumbnail';
  sortOrder: number;
}

interface ImageManagerProps {
  entityType: string;
  entityId: string;
  images: ManagedImage[];
  imageType?: 'main' | 'gallery' | 'thumbnail';
  multiple?: boolean;
  maxImages?: number;
  onChange?: (images: ManagedImage[]) => void;
  onUploadComplete?: (image: ManagedImage) => void;
}

interface PendingCompression {
  file: File;
  preview: string;
  result?: CompressionResult;
  compressing: boolean;
  error?: string;
}

interface UploadingImage {
  file: File;
  preview: string;
  progress: number;
  error?: string;
}

export function ImageManager({
  entityType,
  entityId,
  images,
  imageType = 'gallery',
  multiple = true,
  maxImages = 10,
  onChange,
  onUploadComplete,
}: ImageManagerProps) {
  const [uploading, setUploading] = useState<UploadingImage[]>([]);
  const [pendingCompressions, setPendingCompressions] = useState<PendingCompression[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      uploading.forEach(u => URL.revokeObjectURL(u.preview));
    };
  }, [uploading]);

  const validateFile = useCallback((file: File): string | null => {
    if (!CONTENT_CONSTRAINTS.allowedFormats.includes(file.type)) {
      return 'Invalid format. Use WebP, JPEG, PNG, or GIF';
    }
    if (file.size > CONTENT_CONSTRAINTS.maxSizeBytes) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      return `File too large (${sizeMB}MB). Maximum: 2MB`;
    }
    return null;
  }, []);

  const uploadFile = useCallback(async (file: File, thumbnail?: File): Promise<ManagedImage | null> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entityType', entityType);
    formData.append('entityId', entityId);
    formData.append('imageType', imageType);
    if (thumbnail) {
      formData.append('thumbnail', thumbnail);
    }

    try {
      const response = await fetch(`${API_URL}/admin/images`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Upload failed');
      }

      return {
        url: data.data.url,
        r2Path: data.data.r2Path,
        imageType,
        sortOrder: images.length,
      };
    } catch (error) {
      throw error;
    }
  }, [entityType, entityId, imageType, images.length]);

  const handleFiles = useCallback(async (files: FileList) => {
    const validFiles: { file: File; preview: string }[] = [];

    // Validate all files first
    for (const file of Array.from(files)) {
      if (!multiple && images.length >= 1) {
        alert('Only one image allowed');
        return;
      }
      if (images.length + validFiles.length >= maxImages) {
        alert(`Maximum ${maxImages} images allowed`);
        break;
      }

      const error = validateFile(file);
      if (error) {
        alert(error);
        continue;
      }

      validFiles.push({
        file,
        preview: URL.createObjectURL(file),
      });
    }

    if (validFiles.length === 0) return;

    // Start compression for each file
    const pending: PendingCompression[] = validFiles.map(f => ({
      file: f.file,
      preview: f.preview,
      compressing: true,
    }));
    setPendingCompressions(prev => [...prev, ...pending]);

    // Compress each file in parallel
    for (const item of pending) {
      try {
        const result = await compressForUpload(item.file);
        setPendingCompressions(prev =>
          prev.map(p =>
            p.preview === item.preview
              ? { ...p, result, compressing: false }
              : p
          )
        );
      } catch {
        setPendingCompressions(prev =>
          prev.map(p =>
            p.preview === item.preview
              ? { ...p, compressing: false, error: 'Compression failed' }
              : p
          )
        );
      }
    }
  }, [images, multiple, maxImages, validateFile]);

  const confirmUpload = useCallback(async () => {
    const ready = pendingCompressions.filter(p => p.result && !p.error);
    if (ready.length === 0) return;

    // Move to uploading state
    setUploading(prev => [
      ...prev,
      ...ready.map(p => ({
        file: p.result!.compressed.file,
        preview: p.preview,
        progress: 0,
      })),
    ]);
    setPendingCompressions([]);

    // Upload each file
    const newImages: ManagedImage[] = [];

    for (const item of ready) {
      const { compressed, thumbnail } = item.result!;
      try {
        const uploaded = await uploadFile(compressed.file, thumbnail.file);
        if (uploaded) {
          newImages.push(uploaded);
          onUploadComplete?.(uploaded);
        }
      } catch (error) {
        setUploading(prev =>
          prev.map(u =>
            u.preview === item.preview
              ? { ...u, error: error instanceof Error ? error.message : 'Upload failed' }
              : u
          )
        );
      } finally {
        setTimeout(() => {
          URL.revokeObjectURL(item.preview);
          setUploading(prev => prev.filter(u => u.preview !== item.preview || u.error));
        }, 500);
      }
    }

    if (newImages.length > 0) {
      onChange?.([...images, ...newImages]);
    }
  }, [pendingCompressions, uploadFile, onChange, onUploadComplete, images]);

  const cancelPending = useCallback(() => {
    pendingCompressions.forEach(p => URL.revokeObjectURL(p.preview));
    setPendingCompressions([]);
  }, [pendingCompressions]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
      e.target.value = ''; // Reset input
    }
  }, [handleFiles]);

  const handleDelete = useCallback(async (index: number) => {
    const image = images[index];
    // Freshly-uploaded images carry r2Path; images loaded from a saved record
    // only have a url, so recover the value stored in the images table (r2_path).
    // R2-hosted images store a relative path (url = IMAGE_BASE + path); seeded
    // placeholder images store the full external url verbatim.
    let r2Path = image.r2Path;
    if (!r2Path && image.url && !image.url.startsWith('blob:') && !image.url.startsWith('data:')) {
      const imgBase = (import.meta.env.VITE_IMAGE_URL || '').replace(/\/$/, '');
      if (imgBase && image.url.startsWith(imgBase)) {
        r2Path = image.url.slice(imgBase.length).replace(/^\/+/, '');
      } else {
        // external/full url (e.g. seeded picsum placeholder) — stored as-is
        r2Path = image.url;
      }
    }

    if (!r2Path) {
      // Not persisted yet — just remove from local state
      const newImages = images.filter((_, i) => i !== index);
      onChange?.(newImages);
      return;
    }

    if (!confirm('Delete this image?')) return;

    try {
      // Use a query param so arbitrary values (incl. full URLs) transport cleanly.
      const response = await fetch(`${API_URL}/admin/images?path=${encodeURIComponent(r2Path)}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error?.message || 'Delete failed');
      }

      const newImages = images.filter((_, i) => i !== index);
      onChange?.(newImages);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to delete image');
    }
  }, [images, onChange]);

  // Drag and drop reordering
  const handleDragStart = useCallback((index: number) => {
    setDraggedIndex(index);
  }, []);

  const handleDragEnter = useCallback((index: number) => {
    if (draggedIndex === null || draggedIndex === index) return;

    const newImages = [...images];
    const [dragged] = newImages.splice(draggedIndex, 1);
    newImages.splice(index, 0, dragged);

    // Update sort orders
    newImages.forEach((img, i) => {
      img.sortOrder = i;
    });

    onChange?.(newImages);
    setDraggedIndex(index);
  }, [draggedIndex, images, onChange]);

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
  }, []);

  const canAddMore = multiple ? images.length < maxImages : images.length === 0;

  return (
    <div className="space-y-4">
      {/* Requirements info */}
      <div className="bg-gray-50 rounded-lg p-3 text-sm">
        <div className="font-medium text-gray-700 mb-1">Image Requirements:</div>
        <ul className="text-gray-500 space-y-0.5">
          <li>Max file size: 2MB per image</li>
          <li>Formats: WebP (recommended), JPEG, PNG, GIF</li>
          <li>Images are auto-compressed to WebP before upload</li>
          {maxImages > 1 && <li>Maximum {maxImages} images</li>}
        </ul>
      </div>

      {/* Compression preview */}
      {pendingCompressions.length > 0 && (
        <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
          <div className="font-medium text-gray-700 mb-3">Compression Preview</div>
          <div className="space-y-3">
            {pendingCompressions.map((item, index) => (
              <div key={index} className="flex items-center gap-3 bg-white rounded-lg p-3 border border-gray-100">
                <img
                  src={item.preview}
                  alt="Preview"
                  className="w-16 h-16 object-cover rounded-md flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-700 truncate">{item.file.name}</div>
                  {item.compressing && (
                    <div className="flex items-center gap-2 text-xs text-amber-600 mt-1">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Compressing...
                    </div>
                  )}
                  {item.error && (
                    <div className="flex items-center gap-1 text-xs text-red-500 mt-1">
                      <AlertCircle className="w-3 h-3" />
                      {item.error}
                    </div>
                  )}
                  {item.result && (
                    <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                      <div className="flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3 text-green-500" />
                        <span>
                          {formatFileSize(item.result.original.size)} → {formatFileSize(item.result.compressed.size)}
                          {' '}
                          <span className="text-green-600 font-medium">(-{item.result.savings}%)</span>
                        </span>
                      </div>
                      <div className="text-gray-400">
                        Thumbnail: {formatFileSize(item.result.thumbnail.size)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              onClick={confirmUpload}
              disabled={pendingCompressions.some(p => p.compressing)}
              className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Upload {pendingCompressions.filter(p => p.result).length} image(s)
            </button>
            <button
              type="button"
              onClick={cancelPending}
              className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Current images */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {images.map((image, index) => (
            <div
              key={image.url}
              draggable={multiple}
              onDragStart={() => handleDragStart(index)}
              onDragEnter={() => handleDragEnter(index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => e.preventDefault()}
              className={`
                relative group rounded-lg overflow-hidden border-2 transition-all
                ${draggedIndex === index ? 'border-gold-400 opacity-50' : 'border-transparent'}
                ${multiple ? 'cursor-move' : ''}
              `}
            >
              <img
                src={image.url}
                alt={image.altEn || `Image ${index + 1}`}
                className="w-full h-32 object-cover"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                {multiple && (
                  <div className="p-1.5 bg-white/90 rounded-lg">
                    <GripVertical className="w-4 h-4 text-gray-600" />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => handleDelete(index)}
                  className="p-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              {index === 0 && imageType === 'gallery' && (
                <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-gold-500 text-white text-xs rounded">
                  Main
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Uploading images */}
      {uploading.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {uploading.map((item, index) => (
            <div
              key={index}
              className={`relative rounded-lg overflow-hidden border-2 ${
                item.error ? 'border-red-300' : 'border-gray-200'
              }`}
            >
              <img
                src={item.preview}
                alt="Uploading"
                className="w-full h-32 object-cover opacity-50"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                {item.error ? (
                  <div className="p-2 bg-red-100 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  </div>
                ) : (
                  <div className="p-2 bg-white/90 rounded-lg">
                    <Loader2 className="w-5 h-5 animate-spin text-gold-600" />
                  </div>
                )}
              </div>
              {item.error && (
                <div className="absolute bottom-0 left-0 right-0 p-1 bg-red-100 text-red-600 text-xs text-center truncate">
                  {item.error}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload zone */}
      {canAddMore && (
        <div
          onClick={handleClick}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-gold-400 hover:bg-gold-50 transition-colors"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={CONTENT_CONSTRAINTS.allowedFormats.join(',')}
            multiple={multiple}
            onChange={handleInputChange}
            className="hidden"
          />
          <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-gray-100 flex items-center justify-center">
            <Upload className="w-5 h-5 text-gray-400" />
          </div>
          <div className="text-sm text-gray-600">
            <span className="font-medium text-gold-600">Click to upload</span> or drag and drop
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {multiple ? `Up to ${maxImages - images.length} more image(s)` : 'Select an image'}
          </div>
        </div>
      )}

      {/* Empty state */}
      {images.length === 0 && uploading.length === 0 && !canAddMore && (
        <div className="text-center py-8 text-gray-500">
          <ImageIcon className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No images</p>
        </div>
      )}
    </div>
  );
}
